import {z} from 'zod';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

import {hasMazdaWarrantyInstallationDetails} from '../utils/hasMazdaWarrantyInstallationDetails';

export const MAINTENANCE_DONE_RIGHT = 'doneRight' as const;
export const MAINTENANCE_NOT_DONE_CORRECTLY = 'notDoneCorrectly' as const;

const addRequiredIssue = (ctx: z.RefinementCtx, path: string) =>
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: [path],
    message: i18n.t('general.errors.mixed.required'),
  });

const isBlank = (value: string | Nullish) => isNilOrEmpty(value?.trim());

const requiredText = () => z.string().trim().nonempty(i18n.t('general.errors.mixed.required'));

// Mileage stays nullish in the base object: a type-level failure there would abort the parse
// before superRefine runs, hiding every conditional error until the next submit.
export const MazdaPreauthSendFormSchema = z
  .object({
    scope: requiredText(),
    warrantyType: requiredText(),
    mileage: z.number().min(0).nullable().optional(),
    vehicleServicedAccordingToManufacturerSpecs: z
      .enum([MAINTENANCE_DONE_RIGHT, MAINTENANCE_NOT_DONE_CORRECTLY])
      .optional(),
    installationDate: z.string().nullable().optional(),
    installationMileage: z.number().min(0).nullable().optional(),
    installationRepairNo: z.string().nullable().optional(),
    isSpecialCausalPart: z.boolean(),
    causalPart: z.string().nullable().optional(),
    specialCausalPart: z.string().nullable().optional(),
    message: requiredText(),
  })
  .superRefine((data, ctx) => {
    if (isNil(data.mileage)) {
      addRequiredIssue(ctx, 'mileage');
    }

    if (isNil(data.vehicleServicedAccordingToManufacturerSpecs)) {
      addRequiredIssue(ctx, 'vehicleServicedAccordingToManufacturerSpecs');
    }

    if (hasMazdaWarrantyInstallationDetails(data.warrantyType)) {
      if (isBlank(data.installationDate)) {
        addRequiredIssue(ctx, 'installationDate');
      }
      if (isNil(data.installationMileage)) {
        addRequiredIssue(ctx, 'installationMileage');
      }
      if (isBlank(data.installationRepairNo)) {
        addRequiredIssue(ctx, 'installationRepairNo');
      }
    }

    if (data.isSpecialCausalPart && isBlank(data.specialCausalPart)) {
      addRequiredIssue(ctx, 'specialCausalPart');
    }

    if (!data.isSpecialCausalPart && isBlank(data.causalPart)) {
      addRequiredIssue(ctx, 'causalPart');
    }
  });

export type MazdaPreauthSendFormType = z.infer<typeof MazdaPreauthSendFormSchema>;
